import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase/client';

interface Company {
  id: string;
  name: string;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

const CSRICPSignalUpload: React.FC = () => {
  const navigate = useNavigate();

  // Company list for selector
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  // Form fields
  const [companyId, setCompanyId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [productContext, setProductContext] = useState('');
  const [icpHypothesis, setIcpHypothesis] = useState('');
  const [contactsFile, setContactsFile] = useState<File | null>(null);
  const [convsFile, setConvsFile] = useState<File | null>(null);

  // Classification state
  const [phase, setPhase] = useState<'form' | 'classifying' | 'done'>('form');
  const [jobId, setJobId] = useState('');
  const [totalRecords, setTotalRecords] = useState(0);
  const [classifiedCount, setClassifiedCount] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);

  const classifyingRef = useRef(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  async function loadCompanies() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: profile } = await supabase
      .from('users').select('role').eq('id', user.id).single();

    let query = supabase.from('companies').select('id, name').order('name');
    // CSRs only see their assigned companies
    if (profile?.role === 'csr') {
      query = query.eq('assigned_csr_id', user.id);
    }

    const { data } = await query;
    setCompanies(data ?? []);
    if (data && data.length === 1) setCompanyId(data[0].id);
    setLoadingCompanies(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (!companyId)       return setFormError('Please select a company.');
    if (!campaignName.trim()) return setFormError('Campaign name is required.');
    if (!productContext.trim()) return setFormError('Product context is required.');
    if (!icpHypothesis.trim()) return setFormError('ICP hypothesis is required.');
    if (!contactsFile)    return setFormError('Contacts CSV is required.');

    const company = companies.find(c => c.id === companyId);

    // Build FormData for Edge Function
    const form = new FormData();
    form.append('company_id', companyId);
    form.append('company_name', company?.name ?? '');
    form.append('campaign_name', campaignName.trim());
    form.append('product_context', productContext.trim());
    form.append('icp_hypothesis', icpHypothesis.trim());
    form.append('contacts_csv', contactsFile);
    if (convsFile) form.append('conversations_csv', convsFile);

    setPhase('classifying');

    try {
      // Call icp-job-start
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token ?? '';

      const startRes = await fetch(
        `${SUPABASE_URL}/functions/v1/icp-job-start`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        }
      );
      const startData = await startRes.json();
      if (!startRes.ok) throw new Error(startData.error ?? 'Failed to start job');

      const { job_id, total_records } = startData as { job_id: string; total_records: number };
      setJobId(job_id);
      setTotalRecords(total_records);
      setClassifiedCount(0);

      // Run classification chunks in loop
      classifyingRef.current = true;
      let count = 0;
      let done  = false;

      while (!done && classifyingRef.current) {
        const chunkRes = await fetch(
          `${SUPABASE_URL}/functions/v1/icp-classify-chunk`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ job_id }),
          }
        );
        const chunkData = await chunkRes.json();
        if (!chunkRes.ok) throw new Error(chunkData.error ?? 'Classification error');

        count = chunkData.classified_count ?? count;
        done  = chunkData.is_done ?? false;
        setClassifiedCount(count);

        if (!done) {
          // Brief pause between chunks to avoid hammering the function
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
      }

      setPhase('done');

    } catch (err) {
      setFormError(String(err));
      setPhase('form');
    }
  }

  // ── Classifying progress screen ───────────────────────────────────────────

  if (phase === 'classifying') {
    const pct = totalRecords > 0 ? Math.round((classifiedCount / totalRecords) * 100) : 0;
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl border border-gray-200 p-10 max-w-md w-full text-center">
          <div className="text-4xl mb-4">⚙️</div>
          <h2 className="text-lg font-semibold text-gray-800 mb-1">Classifying responses…</h2>
          <p className="text-sm text-gray-500 mb-6">
            AI is analyzing each reply for intent and ICP fit. This may take a minute for large batches.
          </p>
          <div className="mb-2 text-sm text-gray-600">{classifiedCount} / {totalRecords} records</div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
            <div
              className="bg-indigo-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-xs text-gray-400">Please keep this tab open while classifying.</p>
        </div>
      </div>
    );
  }

  // ── Done screen ───────────────────────────────────────────────────────────

  if (phase === 'done') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl border border-gray-200 p-10 max-w-md w-full text-center">
          <div className="text-4xl mb-4">✅</div>
          <h2 className="text-lg font-semibold text-gray-800 mb-1">Classification complete</h2>
          <p className="text-sm text-gray-500 mb-6">
            {totalRecords} leads classified. Review flagged records before viewing the verdict.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate(`/csr-icp-signal/${jobId}/review`)}
              className="bg-amber-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-amber-600"
            >
              Review Flagged Records →
            </button>
            <button
              onClick={() => navigate(`/csr-icp-signal/${jobId}/verdict`)}
              className="text-gray-600 border border-gray-300 px-5 py-2 rounded-lg text-sm hover:bg-gray-50"
            >
              Skip to Verdict
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Upload form ───────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back */}
        <button
          onClick={() => navigate('/csr-icp-signal')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1"
        >
          ← All Analyses
        </button>

        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <h1 className="text-xl font-bold text-gray-900 mb-1">🔬 New ICP Signal Analysis</h1>
          <p className="text-sm text-gray-500 mb-6">
            Upload a contacts CSV and describe the campaign. Claude will classify each reply by intent and ICP fit.
          </p>

          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-5 text-sm text-red-700">
              {formError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Company */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              {loadingCompanies ? (
                <div className="text-sm text-gray-400">Loading…</div>
              ) : (
                <select
                  value={companyId}
                  onChange={e => setCompanyId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  required
                >
                  <option value="">Select a company…</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Campaign name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign / sequence name</label>
              <input
                type="text"
                value={campaignName}
                onChange={e => setCampaignName(e.target.value)}
                placeholder="e.g. Cooby Q2 EMEA — Sales Director"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                required
              />
            </div>

            {/* Product context */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product context</label>
              <p className="text-xs text-gray-400 mb-1">
                1–3 sentences describing the product and its core use case. Used in the AI classification prompt.
              </p>
              <textarea
                value={productContext}
                onChange={e => setProductContext(e.target.value)}
                rows={4}
                placeholder="e.g. Cooby is a WhatsApp-to-CRM sync tool that captures WhatsApp conversations and syncs them into CRMs (HubSpot, Salesforce) so team leaders can track pipeline activity..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-y"
                required
              />
            </div>

            {/* ICP hypothesis */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ICP hypothesis</label>
              <p className="text-xs text-gray-400 mb-1">
                Who is in-ICP and what makes someone off-ICP? Be behavioral, not just vertical. This defines how the AI judges ICP fit.
              </p>
              <textarea
                value={icpHypothesis}
                onChange={e => setIcpHypothesis(e.target.value)}
                rows={4}
                placeholder="e.g. In-ICP: teams that use (or want to use) WhatsApp to communicate with the people they are converting or placing. The qualifying signal is WhatsApp-based pipeline outreach, NOT industry. Disqualifiers: (1) team explicitly does not use WhatsApp and has no interest; (2) respondent is a WhatsApp/CRM vendor (direct competitor)."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-y"
                required
              />
            </div>

            {/* Contacts CSV */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contacts CSV <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-400 mb-1">
                Expected columns: First Name, Last Name, LinkedIn URL, Title, Company, Reply (optional — can be blank if supplying Conversations CSV).
              </p>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={e => setContactsFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                required
              />
              {contactsFile && (
                <p className="text-xs text-gray-500 mt-1">✓ {contactsFile.name} ({Math.round(contactsFile.size / 1024)} KB)</p>
              )}
            </div>

            {/* Conversations CSV (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Conversations CSV <span className="text-gray-400">(optional)</span>
              </label>
              <p className="text-xs text-gray-400 mb-1">
                Upload a Dripify conversations export to enrich the reply_text field. Joined on LinkedIn URL. Helpful when the contacts CSV has empty Reply columns.
              </p>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={e => setConvsFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-gray-50 file:text-gray-600 hover:file:bg-gray-100"
              />
              {convsFile && (
                <p className="text-xs text-gray-500 mt-1">✓ {convsFile.name} ({Math.round(convsFile.size / 1024)} KB)</p>
              )}
            </div>

            {/* Submit */}
            <div className="pt-2 flex gap-3">
              <button
                type="submit"
                className="bg-indigo-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
              >
                Start Classification
              </button>
              <button
                type="button"
                onClick={() => navigate('/csr-icp-signal')}
                className="text-gray-600 border border-gray-300 px-5 py-2.5 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>

        {/* Cost estimate */}
        <div className="mt-4 bg-indigo-50 rounded-lg p-4 text-sm text-indigo-700">
          <strong>Estimated cost:</strong> ~$0.008 per 50 records (Claude Haiku).
          A 200-record campaign costs roughly $0.03; a 3,000-record campaign roughly $0.50.
        </div>
      </div>
    </div>
  );
};

export default CSRICPSignalUpload;
